import { useEffect, useState } from 'react'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'
import Stack from 'react-bootstrap/Stack'
import Table from 'react-bootstrap/Table'

import { getFnbEodCounts, saveFnbEodCounts } from '../../utilities/api'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { formatInteger } from './fnbEodReportUtils'

export default function FnbEodReportCountModal({
  show,
  onHide,
  organizationUUID,
  eventUUID,
  locationId,
  reportDate,
  inventorySetupRequired,
  onSaved,
}) {
  const [items, setItems] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [loadError, setLoadError] = useState(null)
  const [saveError, setSaveError] = useState(null)

  useEffect(() => {
    if (!show || !organizationUUID || !eventUUID || !locationId) {
      setItems([])
      return
    }

    let cancelled = false
    setIsLoading(true)
    setLoadError(null)

    getFnbEodCounts(organizationUUID, {
      eventUUID,
      locationId,
      reportDate,
    })
      .then((res) => {
        if (cancelled) return
        setItems(Array.isArray(res?.data?.items) ? res.data.items : [])
      })
      .catch((err) => {
        if (cancelled) return
        console.error('Failed to load closing counts', err)
        setLoadError('Could not load closing count worksheet for this stand.')
        setItems([])
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [show, organizationUUID, eventUUID, locationId, reportDate])

  const updateItem = (itemId, value) => {
    setItems((current) =>
      current.map((item) =>
        item.itemId === itemId
          ? {
              ...item,
              actualQuantity: value === '' ? '' : Number(value),
            }
          : item
      )
    )
  }

  const handleSave = async () => {
    setIsSaving(true)
    setSaveError(null)

    try {
      const entries = items.map((item) => ({
        itemId: item.itemId,
        actualQuantity:
          item.actualQuantity === '' || item.actualQuantity == null
            ? null
            : Number(item.actualQuantity),
      }))

      await saveFnbEodCounts(organizationUUID, {
        eventUUID,
        locationId,
        reportDate,
        entries,
      })

      onSaved?.()
      onHide()
    } catch (err) {
      console.error('Failed to save closing counts', err)
      setSaveError('Could not save closing counts. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Enter Closing Counts</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small">
          Record the physical inventory on hand at close. The variance report
          compares these counts against the expected ending inventory from sales
          and waste.
        </p>

        {inventorySetupRequired ? (
          <Alert variant="warning">
            Set beginning inventory first so expected ending values can be
            calculated.
          </Alert>
        ) : null}

        {loadError ? <Alert variant="danger">{loadError}</Alert> : null}
        {saveError ? <Alert variant="danger">{saveError}</Alert> : null}

        {isLoading ? (
          <PageLoadingContainer style="without-sidebar" />
        ) : !items.length ? (
          <p className="mb-0 text-muted">
            {inventorySetupRequired
              ? 'Expected ending inventory is not available yet for this stand.'
              : 'No inventory-tracked items are available for closing counts.'}
          </p>
        ) : (
          <div className="table-container border-bottom-0 fnb-eod-table-scroll">
            <Table responsive className="mb-0 fnb-eod-table">
              <thead>
                <tr>
                  <th className="text-left uppercase text-body">Item</th>
                  <th className="text-right uppercase text-body">Expected</th>
                  <th className="text-right uppercase text-body">
                    Actual Count
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.itemId}>
                    <td className="text-left">{item.itemName}</td>
                    <td className="text-right">
                      {formatInteger(item.expectedEnding)}
                    </td>
                    <td className="text-right">
                      <Form.Control
                        type="number"
                        min="0"
                        step="1"
                        size="sm"
                        className="fnb-eod-inventory-input"
                        value={
                          item.actualQuantity == null ? '' : item.actualQuantity
                        }
                        onChange={(event) =>
                          updateItem(item.itemId, event.target.value)
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Stack direction="horizontal" gap={2} className="ms-auto">
          <Button
            variant="outline-secondary"
            onClick={onHide}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={
              isSaving || isLoading || !items.length || inventorySetupRequired
            }
          >
            {isSaving ? 'Saving…' : 'Save closing counts'}
          </Button>
        </Stack>
      </Modal.Footer>
    </Modal>
  )
}
